import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { fusionarLineas, conflictosFusion } from "@/lib/fusionLineas";

// Diálogo inline de fusión de 2 líneas: muestra qué se conserva y pide decisión en conflictos.
export function FusionLineasPanel({ a, b, idxA, idxB, espacios, onConfirm, onCancel }) {
  const conflictos = conflictosFusion(a, b);
  const [elecciones, setElecciones] = useState(() => Object.fromEntries(conflictos.map((c) => [c.campo, "a"])));
  const preview = fusionarLineas(a, b, elecciones);
  const nombreEsp = (id) => espacios.find((e) => e.id === id)?.nombre || id || "— sin espacio —";
  return (
    <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-3 space-y-2 text-xs" data-testid="fusion-panel">
      <div className="font-semibold text-amber-200">Fusionar línea #{idxA + 1} + línea #{idxB + 1}</div>
      {conflictos.length > 0 && (
        <div className="space-y-1.5" data-testid="fusion-conflictos">
          <div className="text-amber-300">⚠ Conflictos: elige qué conservar</div>
          {conflictos.map((c) => (
            <div key={c.campo} className="flex flex-wrap items-center gap-2" data-testid={`fusion-conflicto-${c.campo}`}>
              <span className="text-zinc-400 w-14">{c.label}:</span>
              {["a", "b"].map((k) => (
                <label key={k} className={`px-2 py-0.5 rounded border cursor-pointer ${elecciones[c.campo] === k ? "bg-amber-500/25 border-amber-500/60 text-amber-100" : "border-zinc-700 text-zinc-300"}`}>
                  <input type="radio" className="mr-1 accent-amber-500" checked={elecciones[c.campo] === k}
                    onChange={() => setElecciones({ ...elecciones, [c.campo]: k })} data-testid={`fusion-elegir-${c.campo}-${k}`} />
                  #{(k === "a" ? idxA : idxB) + 1}: {c.campo === "espacio_id" ? nombreEsp(k === "a" ? a.espacio_id : b.espacio_id) : (k === "a" ? c.a : c.b)}
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="bg-zinc-950/60 rounded p-2 space-y-0.5" data-testid="fusion-preview">
        <div className="text-zinc-500 uppercase text-[9px] tracking-wider">Resultado</div>
        <div><span className="text-zinc-500">Espacio:</span> {nombreEsp(preview.espacio_id)}</div>
        <div><span className="text-zinc-500">Fecha:</span> {preview.fecha_programada || "—"} · <span className="text-zinc-500">Tipos:</span> {preview.tipos.join(", ")}</div>
        <div><span className="text-zinc-500">Descripción:</span> {preview.descripcion || "—"}</div>
        <div><span className="text-zinc-500">Componentes ({preview.componentes.length}):</span> {preview.componentes.map((c) => `${c.material || c.descripcion || "?"}${c.fabricado_por === "dnf" ? " (DNF)" : " (cliente)"}`).join(" · ") || "—"}</div>
        {preview._avisos.length > 0 && <div className="text-amber-300">Avisos conservados: {preview._avisos.length}</div>}
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs text-zinc-400">Cancelar</Button>
        <Button size="sm" onClick={() => onConfirm(elecciones)} data-testid="fusion-confirmar"
          className="h-7 text-xs bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Fusionar</Button>
      </div>
    </div>
  );
}

// Banner de posibles duplicados (A): fuerte = hash/message-id; posible = asunto+cliente+fecha+espacios.
export function DuplicadosBanner({ dups, continuar, onContinuar, onAbrir, onCancelar }) {
  if (!dups || (!dups.exactos?.length && !dups.posibles?.length)) return null;
  const fuerte = dups.exactos?.length > 0;
  return (
    <div className={`rounded-lg p-3 text-xs space-y-2 border ${fuerte ? "bg-red-500/10 border-red-500/50" : "bg-amber-500/10 border-amber-500/40"}`} data-testid="duplicados-banner">
      <div className={`font-semibold ${fuerte ? "text-red-200" : "text-amber-200"}`}>
        {fuerte ? "⛔ Posible petición ya procesada · COINCIDENCIA FUERTE (mismo correo)" : "⚠️ Posible petición ya procesada"}
      </div>
      {[...(dups.exactos || []), ...(dups.posibles || [])].map((d) => (
        <div key={d.peticion_id} className="flex items-center justify-between gap-2 bg-zinc-950/50 rounded px-2 py-1" data-testid={`duplicado-${d.peticion_id}`}>
          <div className="min-w-0">
            <span className={`font-mono ${d.fuerte ? "text-red-200" : "text-amber-100"}`}>{d.codigo}</span> <span className="text-zinc-200">{d.nombre}</span>
            <span className="text-zinc-500"> · {d.estado} · {(d.created_at || "").slice(0, 10)}</span>
            <div className="text-[10px] text-zinc-400">{d.fuerte ? "FUERTE · " : ""}{d.motivo}</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => onAbrir(d.peticion_id)} data-testid={`duplicado-abrir-${d.peticion_id}`}
            className="h-6 text-[10px] bg-transparent border-zinc-600 shrink-0">Abrir existente</Button>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2 pt-1">
        <label className="flex items-center gap-2 text-zinc-200 cursor-pointer">
          <input type="checkbox" checked={!!continuar} onChange={(e) => onContinuar(e.target.checked)} data-testid="duplicado-continuar-check" className="accent-red-500" />
          He revisado los candidatos y quiero <b>continuar creando una petición nueva</b>
        </label>
        <Button size="sm" variant="ghost" onClick={onCancelar} className="h-6 text-[10px] text-zinc-400">Cancelar</Button>
      </div>
    </div>
  );
}
